import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Reliable network status detection service
 * Uses multiple methods to detect connectivity, especially reliable on iOS Safari
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkStatusService {
  private readonly onlineSubject = new BehaviorSubject<boolean>(this.checkInitialStatus());
  public readonly online$: Observable<boolean> = this.onlineSubject.asObservable();

  constructor() {
    this.setupListeners();
    // Do an initial real connectivity check after a short delay
    setTimeout(() => this.checkConnectivity(), 1000);
  }

  /**
   * Get current online status
   * More reliable than navigator.onLine alone (which is broken on iOS)
   */
  public isOnline(): boolean {
    return this.onlineSubject.value;
  }

  private checkInitialStatus(): boolean {
    // Start with navigator.onLine as baseline
    if (typeof navigator === 'undefined') {
      return true;
    }
    return navigator.onLine;
  }

  private setupListeners(): void {
    if (globalThis.window === undefined) return;

    // Listen to standard online/offline events
    const window = globalThis.window;
    window.addEventListener('online', () => this.handleOnlineEvent());
    window.addEventListener('offline', () => this.handleOfflineEvent());

    // iOS Safari specific: Listen to visibility change
    // When app comes to foreground, check connectivity
    document.addEventListener('visibilitychange', () => {
      if (document.hidden === false) {
        this.checkConnectivity();
      }
    });

    // iOS Safari specific: Listen to page show event (back/forward navigation)
    window.addEventListener('pageshow', () => {
      this.checkConnectivity();
    });
  }

  private handleOnlineEvent(): void {
    console.log('📶 Online event detected');
    // Verify with actual connectivity check
    this.checkConnectivity();
  }

  private handleOfflineEvent(): void {
    console.log('📵 Offline event detected');
    this.onlineSubject.next(false);
  }

  /**
   * Actually test connectivity by making a real network request
   * This is the only reliable way on iOS Safari
   */
  private async checkConnectivity(): Promise<void> {
    try {
      // Try to fetch a small resource with cache busting
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch('/assets/images/Logo.png?' + Date.now(), {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const isOnline = response.ok;
      if (this.onlineSubject.value !== isOnline) {
        console.log(`📶 Network status changed: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
        this.onlineSubject.next(isOnline);
      }
    } catch (error: unknown) {
      // Network request failed = offline
      if (this.onlineSubject.value !== false) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.log('📵 Network check failed: OFFLINE', errorMessage);
        this.onlineSubject.next(false);
      }
    }
  }

  /**
   * Force a connectivity check
   * Useful before critical operations like submission
   */
  public async forceCheck(): Promise<boolean> {
    await this.checkConnectivity();
    return this.isOnline();
  }
}

